import React, { useState } from 'react';
import { Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import {
  LockClosedIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/solid';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import PublicNavbar from '../common/PublicNavbar';
import PublicFooter from '../common/PublicFooter';
import { emailAPI } from '../../services/api';

const inputStyle: React.CSSProperties = {
  borderRadius: '10px', border: '1.5px solid #e2e8f0', padding: '12px 16px',
  fontSize: '0.95rem', transition: 'border-color 0.2s',
};
const lblStyle: React.CSSProperties = { fontWeight: 600, color: '#334155', fontSize: '0.88rem', marginBottom: '6px' };

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!token) {
      setError('Invalid or missing reset token.');
      return;
    }

    setLoading(true);
    try {
      await emailAPI.confirmPasswordReset(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Failed to reset password. The link may have expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── Success state ── */
  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicNavbar solid />
        <div style={{
          flex: 1, background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #3b82f6 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          paddingTop: '100px', padding: '100px 1rem 3rem',
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '480px', width: '100%',
            padding: 'clamp(32px, 6vw, 48px)', textAlign: 'center',
            boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1.25rem',
            }}>
              <CheckCircleIcon style={{ width: '40px', height: '40px', color: '#059669' }} />
            </div>
            <h3 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.5rem' }}>Password reset!</h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Your password has been updated successfully. You can now log in with your new password.
            </p>
            <Link
              to="/customer-login"
              className="btn d-inline-flex align-items-center gap-2"
              style={{
                borderRadius: '12px', padding: '12px 32px', fontWeight: 700, fontSize: '1rem',
                background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white',
                border: 'none', boxShadow: '0 4px 15px rgba(30,58,138,0.3)', textDecoration: 'none',
              }}
            >
              Go to Login
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  /* ── Form state ── */
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar solid />

      <div style={{ flex: 1, paddingTop: '72px' }}>
        <Row className="g-0 m-0" style={{ minHeight: 'calc(100vh - 72px)' }}>

          {/* Left panel – gradient hero (desktop only) */}
          <Col lg={5} className="d-none d-lg-flex" style={{
            background: 'linear-gradient(160deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            padding: 'clamp(40px, 5vw, 80px)', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '260px', height: '260px', borderRadius: '50%', background: 'rgba(59,130,246,0.12)' }} />
            <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(59,130,246,0.08)' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div className="d-flex align-items-center gap-2 mb-4">
                <BuildingStorefrontIcon style={{ width: '28px', height: '28px', color: '#60a5fa' }} />
                <span style={{ fontSize: '1.15rem', fontWeight: 700, color: 'white' }}>Sourdoughsy</span>
              </div>

              <h2 style={{ color: 'white', fontWeight: 800, fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', lineHeight: 1.3, marginBottom: '0.75rem' }}>
                Almost there — set your new password
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', lineHeight: 1.6, marginBottom: '2.5rem' }}>
                Choose a strong password to secure your account.
              </p>

              <div className="d-flex flex-column gap-3">
                {[
                  { icon: LockClosedIcon, title: 'Min. 8 characters', desc: 'Use a mix of letters, numbers and symbols' },
                  { icon: ShieldCheckIcon, title: 'Secure & encrypted', desc: 'Your password is hashed — we never store the plain text' },
                  { icon: KeyIcon, title: 'One-time link', desc: 'This reset link expires after use for your safety' },
                ].map((b) => (
                  <div key={b.title} className="d-flex align-items-start gap-3">
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                      background: 'rgba(255,255,255,0.08)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <b.icon style={{ width: '20px', height: '20px', color: '#60a5fa' }} />
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 700, fontSize: '0.92rem', marginBottom: '1px' }}>{b.title}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.83rem', lineHeight: 1.45 }}>{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          {/* Right – Form */}
          <Col lg={7} xs={12} style={{
            background: '#f8fafc', display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: 'clamp(24px, 4vw, 48px)',
          }}>
            <div style={{ maxWidth: '440px', width: '100%' }}>
              {/* Mobile-only branding */}
              <div className="d-lg-none text-center mb-4">
                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                  <BuildingStorefrontIcon style={{ width: '24px', height: '24px', color: '#1e3a8a' }} />
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e3a8a' }}>Sourdoughsy</span>
                </div>
              </div>

              <h2 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '4px', fontSize: 'clamp(1.4rem, 3vw, 1.7rem)' }}>
                Set your new password
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1.75rem' }}>
                Choose a strong password for your account.
              </p>

              {error && (
                <Alert variant="danger" className="py-2 px-3" style={{ fontSize: '0.88rem', borderRadius: '10px' }}>{error}</Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label style={lblStyle}>New Password</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      required
                      minLength={8}
                      autoFocus
                      style={{ ...inputStyle, paddingRight: '48px' }}
                    />
                    <button type="button" tabIndex={-1} onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '1px', top: '1px', bottom: '1px', width: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '0 9px 9px 0' }}>
                      {showPassword ? <EyeSlashIcon style={{ width: '18px', height: '18px', color: '#64748b' }} /> : <EyeIcon style={{ width: '18px', height: '18px', color: '#64748b' }} />}
                    </button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={lblStyle}>Confirm Password</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      required
                      minLength={8}
                      style={{ ...inputStyle, paddingRight: '48px' }}
                    />
                    <button type="button" tabIndex={-1} onClick={() => setShowConfirm(!showConfirm)}
                      style={{ position: 'absolute', right: '1px', top: '1px', bottom: '1px', width: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '0 9px 9px 0' }}>
                      {showConfirm ? <EyeSlashIcon style={{ width: '18px', height: '18px', color: '#64748b' }} /> : <EyeIcon style={{ width: '18px', height: '18px', color: '#64748b' }} />}
                    </button>
                  </div>
                </Form.Group>

                <Button type="submit" disabled={loading || !password || !confirmPassword}
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    borderRadius: '12px', padding: '14px', fontWeight: 700, fontSize: '1rem',
                    background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white',
                    border: 'none', boxShadow: '0 4px 15px rgba(30,58,138,0.25)', minHeight: '50px',
                  }}>
                  {loading ? <Spinner size="sm" animation="border" className="me-1" /> : <LockClosedIcon style={{ width: '20px', height: '20px' }} />}
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Form>

              <p className="text-center mt-3" style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: 0 }}>
                Remember your password?{' '}
                <Link to="/customer-login" style={{ color: '#1e3a8a', fontWeight: 600, textDecoration: 'none' }}>
                  Back to Login
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <PublicFooter />
    </div>
  );
};

export default ResetPassword;
